'use client';
import { useState } from 'react';
import { Button, useToast, SkeletonBox } from '@practium/ui';
import { DocumentViewer, useLegalPolicies } from '@practium/features/policy';
import type { PolicyType } from '@practium/features/policy';
import { useAcceptConsents } from '@practium/features/api';
import type { ConsentStatus } from '@practium/features/api';
import { useAuth } from '../../contexts/AuthContext';
import { authStorage } from '../../storage/authStorage';
import styles from './ConsentGateModal.module.css';

const SKELETON_LINES = [
	['40%', '100%', '97%', '91%', '84%'],
	['35%', '100%', '95%', '100%', '88%', '76%'],
	['38%', '100%', '93%', '89%'],
];

const DocumentSkeleton = () => (
	<div className={styles.documentSkeleton}>
		<div className={styles.skeletonStatusRow}>
			<SkeletonBox width="4rem" height="1.2rem" borderRadius="0.25rem" />
			<SkeletonBox width="9rem" height="0.9rem" borderRadius="0.25rem" />
		</div>
		<SkeletonBox
			width="55%"
			height="1.6rem"
			borderRadius="0.375rem"
			style={{ marginBottom: '0.625rem' }}
		/>
		<SkeletonBox
			width="82%"
			height="1rem"
			borderRadius="0.25rem"
			style={{ marginBottom: '1.5rem' }}
		/>
		<div className={styles.skeletonMetaRow}>
			<SkeletonBox width="6.5rem" height="0.8rem" borderRadius="0.25rem" />
			<SkeletonBox width="6.5rem" height="0.8rem" borderRadius="0.25rem" />
		</div>
		{SKELETON_LINES.map((lines, sectionIdx) => (
			<div key={sectionIdx} className={styles.skeletonSection}>
				{lines.map((width, lineIdx) => (
					<SkeletonBox
						key={lineIdx}
						width={width}
						height="0.8rem"
						borderRadius="0.25rem"
						style={{ marginBottom: lineIdx === 0 ? '0.75rem' : '0.4rem' }}
					/>
				))}
			</div>
		))}
	</div>
);

interface ConsentGateModalProps {
	consentStatus: ConsentStatus;
}

export const ConsentGateModal = ({ consentStatus }: ConsentGateModalProps) => {
	const [selectedTab, setSelectedTab] = useState<PolicyType | null>(null);
	const { activeLegalLinks, isLoading: policiesLoading } = useLegalPolicies();
	const { mutate: acceptConsents, isPending } = useAcceptConsents();
	const { logout, isAuthenticated } = useAuth();
	const toast = useToast();

	const handleAccept = () => {
		const consents = consentStatus.requiredConsents.map((c) => ({
			policyVersionId: c.policyVersionId,
			accepted: true,
		}));

		acceptConsents(
			{ consents },
			{
				onError: () => {
					toast.error('Não foi possível registrar seu aceite. Tente novamente.');
				},
			}
		);
	};

	const handleCancel = async () => {
		if (isAuthenticated) {
			await logout();
		} else {
			authStorage.clearSession();
			globalThis.location.reload();
		}
	};

	const activeTab = selectedTab ?? activeLegalLinks[0]?.key ?? null;
	const activePolicy = activeLegalLinks.find((link) => link.key === activeTab)?.policy ?? null;

	return (
		<div className={styles.modalWrapper} role="presentation">
			<div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="consent-title">
				<div className={styles.header}>
					<h2 id="consent-title" className={styles.title}>
						Termos e Políticas
					</h2>
					<p className={styles.description}>
						Para continuar usando o Practium, você precisa aceitar os termos e políticas vigentes.
					</p>
				</div>

				<div className={styles.body}>
					<div className={styles.tabsSection}>
						<div className={styles.tabs}>
							{activeLegalLinks.map((link) => (
								<button
									key={link.key}
									type="button"
									className={`${styles.tab} ${activeTab === link.key ? styles.tabActive : ''}`}
									onClick={() => setSelectedTab(link.key)}
								>
									{link.policy?.title || link.label}
								</button>
							))}
						</div>
					</div>
					<div className={styles.documentWrapper}>
						{policiesLoading ? (
							<DocumentSkeleton />
						) : (
							<DocumentViewer policy={activePolicy} isModal />
						)}
					</div>
				</div>

				<div className={styles.actions}>
					<span className={styles.actionsNote}>
						Ao clicar em aceitar, você concorda com todos os termos e políticas listados.
					</span>
					<Button
						variant="cancel"
						className={styles.cancelBtn}
						onClick={handleCancel}
						disabled={isPending}
					>
						Cancelar
					</Button>
					<Button
						variant="primary"
						className={styles.acceptBtn}
						onClick={handleAccept}
						isLoading={isPending}
					>
						Li e aceito os termos e políticas
					</Button>
				</div>
			</div>
		</div>
	);
};
